import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { useToast } from "../Toast";
import { FILE_UPLOAD_CONFIG, VALIDATION_MESSAGES } from "../../utils/constants";
import { formatFileSize } from "../../utils/formatters";

const FileUpload = ({ uploading, onUploadSuccess }) => {
  const toast = useToast();
  const [fileInput, setFileInput] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    if (!file) {
      toast.error(VALIDATION_MESSAGES.FILE_REQUIRED);
      return false;
    }

    // Check file size
    if (file.size > FILE_UPLOAD_CONFIG.MAX_SIZE_BYTES) {
      toast.error(
        `${VALIDATION_MESSAGES.FILE_TOO_LARGE} ${formatFileSize(
          FILE_UPLOAD_CONFIG.MAX_SIZE_BYTES,
        )}`,
      );
      return false;
    }

    // Check file extension using lastIndexOf for proper parsing
    const lastDotIndex = file.name.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === file.name.length - 1) {
      toast.error(VALIDATION_MESSAGES.FILE_INVALID_EXTENSION);
      return false;
    }

    const extension = file.name.substring(lastDotIndex).toLowerCase();
    if (!FILE_UPLOAD_CONFIG.ALLOWED_EXTENSIONS.includes(extension)) {
      toast.error(
        `${VALIDATION_MESSAGES.FILE_INVALID_TYPE} "${extension}". Allowed types: ${FILE_UPLOAD_CONFIG.ALLOWED_EXTENSIONS.join(", ")}`,
      );
      return false;
    }

    return true;
  };

  const clearFileInput = () => {
    setFileInput(null);
    // Clear the file input element immediately to prevent race conditions
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (validateFile(file)) {
        setFileInput(file);
      } else {
        // Clear input if validation fails
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setFileInput(file);
      }
    }
  };

  const handleUpload = () => {
    if (validateFile(fileInput)) {
      onUploadSuccess(fileInput, clearFileInput);
    }
  };

  return (
    <div className="km-upload-section">
      <div
        className={`km-dropzone ${dragActive ? "active" : ""}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        aria-label="Click to browse and select a file for upload"
      >
        <div className="km-dropzone-content">
          <div className="km-dropzone-icon">📁</div>
          <p className="km-dropzone-text">
            Drag and drop a file here, or click to browse
          </p>
          <p className="km-dropzone-subtext">
            Supported: {FILE_UPLOAD_CONFIG.ALLOWED_EXTENSIONS.join(", ")} (Max{" "}
            {formatFileSize(FILE_UPLOAD_CONFIG.MAX_SIZE_BYTES)})
          </p>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            accept={FILE_UPLOAD_CONFIG.ALLOWED_EXTENSIONS.join(",")}
            style={{ display: "none" }}
            aria-label="File input"
          />
        </div>
      </div>

      {fileInput && (
        <div className="km-file-selected">
          <span className="km-file-name">
            📎 {fileInput.name} ({formatFileSize(fileInput.size)})
          </span>
          <div className="km-file-actions">
            <button
              className="km-btn km-btn-secondary km-btn-sm"
              onClick={clearFileInput}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              className="km-btn km-btn-primary"
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

FileUpload.propTypes = {
  uploading: PropTypes.bool,
  onUploadSuccess: PropTypes.func.isRequired,
};

export default FileUpload;
